import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '../contexts/AuthContext';
import './Taskboard.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const taskboardSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

export default function Taskboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const mountedRef = useRef(true);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      taskboardSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const { data, error: err } = await taskboardSupabase
        .from('departments')
        .select('id, name')
        .order('name', { ascending: true });
      if (err) {
        console.error('[Taskboard] fetchDepartments error', err);
        setDepartments([]);
      } else {
        setDepartments(data || []);
      }
    } catch (e) {
      setDepartments([]);
    }
  }, []);

  const fetchProfiles = useCallback(async () => {
    const { data, error: err } = await taskboardSupabase
      .from('profiles')
      .select('id, full_name')
      .order('full_name', { ascending: true, nullsFirst: false });
    if (err) {
      setProfiles([]);
    } else {
      setProfiles(data || []);
    }
  }, []);

  const fetchMyProjects = useCallback(async (mountedRef) => {
    const isMounted = () => !mountedRef || mountedRef.current;
    if (!user?.id) {
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchPromise = taskboardSupabase
        .from('projects')
        .select('*')
        .eq('project_manager_profile_id', user.id)
        .order('created_at', { ascending: false });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const result = await Promise.race([fetchPromise, timeoutPromise]);
      if (!isMounted()) return;
      const { data, error: err } = result;
      if (err) {
        setError(err.message);
        setProjects([]);
      } else {
        setProjects(data || []);
      }
    } catch (e) {
      if (isMounted()) {
        setError(e?.message || 'Failed to load projects.');
        setProjects([]);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [user?.id]);

  const location = useLocation();

  const refreshSessionAndFetch = useCallback(async () => {
    mountedRef.current = true;
    setError(null);
    try {
      await taskboardSupabase.auth.getSession();
      await runWakeUp();
      await fetchMyProjects(mountedRef);
      if (!mountedRef.current) return;
      await fetchDepartments();
      await fetchProfiles();
    } catch (e) {
      setError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchMyProjects, fetchDepartments, fetchProfiles]);

  useEffect(() => {
    if (location.pathname !== '/taskboard') return;
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      const { error: sessionErr } = await taskboardSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setError(sessionErr.message);
        setLoading(false);
        return;
      }
      if (!user?.id) {
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchMyProjects(mountedRef);
      if (cancelled) return;
      await fetchDepartments();
      await fetchProfiles();
    })();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [location.pathname, user?.id, runWakeUp, fetchMyProjects, fetchDepartments, fetchProfiles]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const openAddModal = () => {
    setAddTitle('');
    setSelectedMemberIds([]);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddTitle('');
    setSelectedMemberIds([]);
  };

  const toggleMember = (id) => {
    setSelectedMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!addTitle.trim() || !user?.id) return;
    setSaving(true);
    setError(null);
    try {
      await runWakeUp();
      const { data: newProject, error: insertErr } = await taskboardSupabase
        .from('projects')
        .insert({
          title: addTitle.trim(),
          project_manager_profile_id: user.id,
          status: 'open',
          progress: 0,
          updated_at: new Date().toISOString(),
        })
        .select('id')
        .single();
      if (insertErr) {
        setError(insertErr.message);
        setSaving(false);
        return;
      }
      const projectId = newProject?.id;
      if (!projectId) {
        setError('Project was created but ID was not returned.');
        setSaving(false);
        return;
      }
      const memberUserIds = [...new Set([user.id, ...selectedMemberIds])];
      const memberRows = memberUserIds.map((user_id) => ({ project_id: projectId, user_id }));
      const { error: membersErr } = await taskboardSupabase.from('project_members').insert(memberRows);
      if (membersErr) {
        console.warn('[Taskboard] project_members insert error', membersErr);
      }
      closeAddModal();
      await fetchMyProjects();
      navigate(`/taskboard/${projectId}`);
    } catch (e) {
      setError(e.message || 'Failed to create project.');
    } finally {
      setSaving(false);
    }
  };

  const getDepartmentName = (id) => {
    if (!id) return '—';
    const d = departments.find((x) => x.id === id);
    return d?.name || '—';
  };

  const statusLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
  const formatNum = (n) => (n != null && n !== '' ? Number(n).toFixed(2) : '—');

  const profilesExcludingSelf = profiles.filter((p) => p.id !== user?.id);

  return (
    <div className="taskboard-page">
      <div className="taskboard-header">
        <div className="taskboard-header-row">
          <div>
            <h1 className="taskboard-title">Taskboard</h1>
            <p className="taskboard-subtitle">Projects where you are the project manager</p>
          </div>
          <div className="taskboard-header-actions">
            <button
              type="button"
              className="taskboard-header-btn taskboard-header-btn-primary"
              onClick={openAddModal}
            >
              Add project
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="taskboard-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="taskboard-loading">Loading your projects…</div>
      ) : projects.length === 0 ? (
        <div className="taskboard-empty">
          <p>You are not assigned as project manager on any projects.</p>
        </div>
      ) : (
        <div className="taskboard-grid">
          {projects.map((project) => (
            <article
              key={project.id}
              className="taskboard-card taskboard-card--clickable"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/taskboard/${project.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/taskboard/${project.id}`);
                }
              }}
            >
              <div className="taskboard-card-header">
                <h3 className="taskboard-card-title">{project.title || 'Untitled project'}</h3>
                <span className={`taskboard-card-status taskboard-card-status--${(project.status || 'open').toLowerCase()}`}>
                  {statusLabel(project.status)}
                </span>
              </div>
              <div className="taskboard-card-body">
                <div className="taskboard-card-row">
                  <span className="taskboard-card-label">Client</span>
                  <span className="taskboard-card-value">{project.client_name || '—'}</span>
                </div>
                <div className="taskboard-card-row">
                  <span className="taskboard-card-label">Department</span>
                  <span className="taskboard-card-value">{getDepartmentName(project.department_id)}</span>
                </div>
                <div className="taskboard-card-row">
                  <span className="taskboard-card-label">Start</span>
                  <span className="taskboard-card-value">{project.start_date || '—'}</span>
                </div>
                <div className="taskboard-card-row">
                  <span className="taskboard-card-label">Deadline</span>
                  <span className="taskboard-card-value taskboard-card-deadline">{project.deadline || '—'}</span>
                </div>
                <div className="taskboard-card-row">
                  <span className="taskboard-card-label">Price</span>
                  <span className="taskboard-card-value">{formatNum(project.price)}</span>
                </div>
              </div>
              <div className="taskboard-card-footer">
                <div className="taskboard-card-progress-wrap">
                  <span className="taskboard-card-progress-label">Progress</span>
                  <div className="taskboard-card-progress-track">
                    <div
                      className="taskboard-card-progress-fill"
                      style={{ width: `${Math.min(100, Math.max(0, project.progress ?? 0))}%` }}
                    />
                  </div>
                  <span className="taskboard-card-progress-pct">{project.progress ?? 0}%</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="taskboard-modal-backdrop" onClick={closeAddModal}>
          <div className="taskboard-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="taskboard-modal-title">Add project</h2>
            <p className="taskboard-modal-note">You will be set as project manager. Add members who can be assigned to tasks.</p>
            <form onSubmit={handleAddProject}>
              <div className="taskboard-form-group">
                <label htmlFor="taskboard-add-title">Project title</label>
                <input
                  id="taskboard-add-title"
                  type="text"
                  value={addTitle}
                  onChange={(e) => setAddTitle(e.target.value)}
                  placeholder="Enter project title"
                  required
                  className="taskboard-form-input"
                  autoFocus
                />
              </div>
              <div className="taskboard-form-group">
                <label>Project members</label>
                <div className="taskboard-members-list">
                  {profilesExcludingSelf.length === 0 ? (
                    <p className="taskboard-members-empty">No other users in the system.</p>
                  ) : (
                    profilesExcludingSelf.map((profile) => (
                      <label key={profile.id} className="taskboard-member-check">
                        <input
                          type="checkbox"
                          checked={selectedMemberIds.includes(profile.id)}
                          onChange={() => toggleMember(profile.id)}
                        />
                        <span>{profile.full_name || profile.id}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="taskboard-modal-actions">
                <button type="button" className="taskboard-modal-btn taskboard-modal-btn-secondary" onClick={closeAddModal}>
                  Cancel
                </button>
                <button type="submit" className="taskboard-modal-btn taskboard-modal-btn-primary" disabled={saving || !addTitle.trim()}>
                  {saving ? 'Creating…' : 'Create project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
